import React, { ComponentProps, useMemo } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import GatsbySeo from 'components/shared/GatsbySeo/GatsbySeo'
import { MetaProps } from 'components/shared/GatsbySeo/types'
import { useGetLinkTranslations } from 'contexts/LanguageSwitcherContext'
import { ContextI18n } from 'utils/helpers'

const ReadSiteMetadata = graphql`
  query ReadSiteMetadata {
    site {
      buildTime
      siteMetadata {
        title
        description
        author
        siteUrl
      }
    }
  }
`

const structuredPageData = {
  '@context': 'http://schema.org/',
  '@type': 'Organization',
  name: 'WHU - Otto Beisheim School of Management – Executive Education',
  alternateName: 'WHU - Otto Beisheim School of Management',
  url: 'https://ee.whu.edu/',
  logo: 'https://ee.whu.edu/logo.svg',
  vatId: 'DE148733174',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+4926165090',
    email: 'whu@whu.edu',
    faxNumber: '+492616509509',
  },
  location: {
    '@type': 'Place',
    hasMap:
      'https://www.google.com/maps/place/WHU+-+Otto+Beisheim+School+of+Management/@50.4003254,7.6113179,17z/data=!3m1!4b1!4m5!3m4!1s0x47be7d3cc638671b:0x73da18b40028eefb!8m2!3d50.4003254!4d7.6135066',
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'DE',
    addressRegion: 'Rheinland-Pfalz',
    addressLocality: 'Vallendar',
    streetAddress: 'Burgpl. 2',
    postalCode: '56179',
  },
}

type PageContext = {
  translations?: Record<string, string>
  i18n: ContextI18n & {
    path?: string
  }
}

const useTranslationMeta = (url: string, context: PageContext) => {}

const Seo: React.FC<
  {
    description?: string
    noindex?: boolean
    structuredData?: any[]
    pageContext: PageContext
    meta?: Queries.MetaFragmentFragment | null
  } & ComponentProps<typeof GatsbySeo>
> = ({
  description,
  title,
  noindex,
  structuredData,
  metaTags,
  htmlAttributes,
  pageContext,
  meta,
  ...props
}) => {
  const { site } =
    useStaticQuery<Queries.ReadSiteMetadataQuery>(ReadSiteMetadata)
  const metaDescription =
    description ?? site?.siteMetadata?.description ?? undefined

  const language = pageContext.i18n.language

  const regexp = /(?<!https:)\/\/+/g
  const { translations } = useGetLinkTranslations(
    pageContext.translations,
    pageContext.i18n.languages,
    pageContext.i18n.path!,
    pageContext.i18n.defaultLanguage!
  )
  const languageAlternates = useMemo(() => {
    const alternatives = []

    for (const lang in translations) {
      if (!translations.hasOwnProperty(lang)) {
        continue
      }

      alternatives.push({
        hrefLang: lang,
        href: `${site?.siteMetadata?.siteUrl!}/${
          translations[lang]
        }`.replaceAll(regexp, '/'),
      })

      if (lang === 'en') {
        alternatives.push({
          hrefLang: 'x-default',
          href: `${site?.siteMetadata?.siteUrl!}/${
            translations[lang]
          }`.replaceAll(regexp, '/'),
        })
      }
    }

    return alternatives
  }, [site, language, translations])

  const canonical =
    meta?.canonical ??
    `${site?.siteMetadata?.siteUrl!}/${
      translations[language] ?? translations.en
    }`.replaceAll(regexp, '/')

  return (
    <>
      <GatsbySeo
        htmlAttributes={
          {
            ...htmlAttributes,
            lang: language,
          } as typeof htmlAttributes
        }
        title={title}
        titleTemplate={`%s`}
        languageAlternates={languageAlternates}
        canonical={canonical}
        metaTags={
          [
            metaDescription
              ? {
                  id: `description`,
                  name: `description`,
                  content: metaDescription,
                }
              : undefined,
            {
              id: 'og:title',
              property: `og:title`,
              content: title,
            },
            metaDescription
              ? {
                  id: `og:description`,
                  property: `og:description`,
                  content: metaDescription,
                }
              : undefined,
            {
              id: `og:type`,
              property: `og:type`,
              content: `website`,
            },
            {
              id: `twitter:card`,
              name: `twitter:card`,
              content: `summary`,
            },
            {
              id: `twitter:creator`,
              name: `twitter:creator`,
              content: site?.siteMetadata?.author ?? undefined,
            },
            {
              id: `twitter:title`,
              name: `twitter:title`,
              content: title?.trim(),
            },
            metaDescription
              ? {
                  id: `twitter:description`,
                  name: `twitter:description`,
                  content: metaDescription,
                }
              : undefined,
            {
              id: `google-site-verification`,
              name: `google-site-verification`,
              content: `-E5brA3hZhRWhWcX6XAw0nE-f4lu66capbZp-QOZPFQ`,
            },
            {
              id: `facebook-domain-verification`,
              name: `facebook-domain-verification`,
              content: `jgade0wj9quu79t5l47q71jcnkaywo`,
            },
            {
              id: `robots`,
              name: `robots`,
              content: noindex ? 'noindex' : 'all',
            },
            {
              id: `googlebot`,
              name: `googlebot`,
              content: noindex ? 'noindex' : 'all',
            },
            ...(metaTags ?? []),
          ].filter(Boolean) as MetaProps[]
        }
        {...props}
      />
      {structuredData?.map((data) => {
        return (
          <script type="application/ld+json">{JSON.stringify(data)}</script>
        )
      })}
      <script type="application/ld+json">
        {JSON.stringify(structuredPageData)}
      </script>
    </>
  )
}

export default Seo
